import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/features/auth/useSession';
import { can } from '@/features/business/capabilities';
import { useBusiness } from '@/features/business/useBusiness';

interface ComposeButtonProps {
  onCompose: () => void;
  fullWidth?: boolean;
}

/**
 * The Post button, which not every account may press.
 *
 * Posting *as* a business is a paid capability (§5), so a business on the free
 * plan gets a locked button rather than a plain one that leads to a refusal.
 * It still opens the composer, because the composer is where the reason lives
 * and where the way out of it is — a disabled button with a tooltip explains
 * nothing to anybody who taps rather than hovers.
 *
 * A person is never shown any of this: the overwhelming majority of accounts
 * have nothing to do with plans, and their button should not carry a padlock
 * because businesses exist.
 */
export function ComposeButton({ onCompose, fullWidth = false }: ComposeButtonProps) {
  const { t } = useTranslation();
  const { user } = useSession();

  const isBusiness = user?.accountType === 'BUSINESS';
  const business = useBusiness(user?.businessSlug ?? '', isBusiness);
  const locked =
    isBusiness && business.data !== undefined && !can(business.data, 'POST_AS_BUSINESS');

  const button = (
    <Button
      fullWidth={fullWidth}
      size="large"
      variant="contained"
      startIcon={locked ? <LockRoundedIcon /> : <AddRoundedIcon />}
      onClick={onCompose}
    >
      {t('composer.post')}
    </Button>
  );

  if (!locked) return button;

  return (
    <Tooltip title={t('composer.postLocked')}>
      {/* A span, because a Tooltip needs a child that can hold a ref and a
          fullWidth Button inside one otherwise loses its width. */}
      <span style={{ display: fullWidth ? 'block' : 'inline-block' }}>{button}</span>
    </Tooltip>
  );
}
